import React, { useCallback, useEffect } from "react"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { useMediaQuery } from "react-responsive"
import { setExperiencesViewMode } from "store/experiencesUi"
import { useExperiencesActions } from "hooks/useExperiencesActions"
import FilterChips from "ui/FilterChips"
import { useExperienceSearch } from "./hooks/useExperienceSearch"
import { usePagination } from "./hooks/usePagination"
import { useExperienceFilters } from "./hooks/useExperienceFilters"
import { useTabCounts } from "./hooks/useTabCounts"
import { useExperienceListingsFetch } from "./hooks/useExperienceListingsFetch"
import { useListingsModals } from "./hooks/useListingsModals"
import { useListingsHandlers } from "./hooks/useListingsHandlers"
import { useListingsDerivedData } from "./hooks/useListingsDerivedData"
import SearchHeader from "./components/SearchHeader"
import FilterButtons from "./components/FilterButtons"
import ExperienceGrid from "./components/ExperienceGrid"
import SearchResults from "./components/SearchResults"
import { AddNewListing } from "./modal/AddNewListing"
import FilterDropdown from "./FilterDropdown"
import EditCategoryModal from "./modal/EditCategory"
import { ListingTable } from "./ListingTable"
import styles from "./index.module.css"

export const ExperienceListings = ({ onSearch }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isCompactDevice = useMediaQuery({ query: "(max-width: 1280px)" })

  const { items: trips, metadata, loading } = useSelector((state) => state.trips)
  const selectedListing = useSelector((state) => state.experiencesUi.viewMode)
  const setSelectedListing = useCallback((mode) => dispatch(setExperiencesViewMode(mode)), [dispatch])

  const { page, pageSize, handlePageChange, handlePageSizeChange, resetPagination } = usePagination(1, 10)
  const filters = useExperienceFilters("published")
  const search = useExperienceSearch({ pageSize, onSearch })
  const [showPagination, setShowPagination] = React.useState(true)

  const { handleDelete, handleDuplicate, handleDeactivate, handleShare, handlePublish, modal, isDuplicateInProgress, duplicatingTripId, processingAction, processingTripId } =
    useExperiencesActions()

  const { categoryOptions, durationOptions, categorizedSearchResults, tripsForMainList } = useListingsDerivedData({ search, trips })

  const { originalStatusCount, refetchCurrent, fetchTripByTitle } = useExperienceListingsFetch({
    activeListingTab: filters.activeListingTab,
    page,
    pageSize,
    metadata,
    searchQuery: search.searchQuery,
    confirmedSearchQuery: search.confirmedSearchQuery,
    debouncedSearchQuery: search.debouncedSearchQuery,
    selectedTrip: search.selectedTrip,
    buildFilterQuery: filters.buildFilterQuery
  })

  const { publishedCount, draftCount, unpublishedCount } = useTabCounts(
    search.selectedTrip,
    search.confirmedSearchQuery,
    filters.activeListingTab,
    metadata,
    originalStatusCount
  )

  const modals = useListingsModals({ setSelectedType: filters.setSelectedType, onActionComplete: refetchCurrent })

  const handlers = useListingsHandlers({
    search,
    filters,
    resetPagination,
    refetchCurrent,
    fetchTripByTitle,
    setShowPagination
  })

  // Sync selectedTrip with full data once it lands in searchTrips
  useEffect(() => {
    if (!search.selectedTrip || search.searchLoading || !search.searchTrips?.length) return
    const full = search.searchTrips.find((trip) => trip.id === search.selectedTrip.id)
    if (full && full !== search.selectedTrip) search.setSelectedTrip(full)
  }, [search])

  const renderSearchResults = () => (
    <SearchResults
      showSearchResults={search.showSearchResults}
      searchQuery={search.searchQuery}
      searchLoading={search.searchLoading}
      searchTrips={search.searchTrips}
      categorizedSearchResults={categorizedSearchResults}
      onTripClick={handlers.handleTripClickWithAPI}
      selectedTrip={search.selectedTrip}
    />
  )

  const sharedActionProps = {
    onDelete: handleDelete,
    onDeactivate: handleDeactivate,
    onDuplicate: handleDuplicate,
    onShare: handleShare,
    onPublish: handlePublish,
    onEditCategory: modals.openEditCategory,
    onActionComplete: refetchCurrent,
    onAddNewExperience: modals.addFromEmptyState,
    activeListingTab: filters.activeListingTab,
    loading,
    metadata,
    showPagination,
    page,
    pageSize,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    isDuplicateInProgress,
    duplicatingTripId,
    processingAction,
    processingTripId
  }

  return (
    <div className="pb-m">
      <SearchHeader
        searchQuery={search.searchQuery}
        onSearchChange={search.handleSearchChange}
        onSearchKeyDown={search.handleSearchKeyDown}
        onClearSearch={handlers.handleClearSearchWithReset}
        onAddNewListing={modals.openAddNewListing}
        renderSearchResults={renderSearchResults}
      />

      <Flex wrap align="center" justify="space-between" className="mb-m">
        <FilterButtons
          selectedListing={selectedListing}
          setSelectedListing={setSelectedListing}
          t={t}
          activeTab={filters.activeTab}
          onTabChange={filters.handleTabChange}
          activeListingTab={filters.activeListingTab}
          onListingTabChange={handlers.handleTabChangeWithReset}
          publishedCount={publishedCount}
          draftCount={draftCount}
          unpublishedCount={unpublishedCount}
          isFilterDropdownOpen={filters.isFilterDropdownOpen}
          onFilterIconClick={filters.handleFilterIconClick}
          onCloseFilterDropdown={filters.handleCloseFilterDropdown}
          FilterDropdown={FilterDropdown}
          pageSize={pageSize}
          onApplyFilters={handlers.handleApplyFilters}
          onClearFilters={handlers.handleClearFilters}
          selectedCategoryIds={filters.selectedCategoryIds}
          selectedDurationTypes={filters.selectedDurationTypes}
          activeFilterCount={filters.activeFilterCount}
        />
      </Flex>

      <Flex wrap gap={8} className={styles.activeFilterChips}>
        <FilterChips
          selectedValues={filters.selectedCategoryIds}
          options={categoryOptions}
          onChange={(next) => handlers.handleApplyFilters({ categoryIds: next, durationTypes: filters.selectedDurationTypes })}
        />
        <FilterChips
          selectedValues={filters.selectedDurationTypes}
          options={durationOptions}
          onChange={(next) => handlers.handleApplyFilters({ categoryIds: filters.selectedCategoryIds, durationTypes: next })}
        />
      </Flex>

      {isCompactDevice || selectedListing === "table" ? (
        <ExperienceGrid {...sharedActionProps} tripsForMainList={tripsForMainList} />
      ) : (
        <ListingTable
          {...sharedActionProps}
          data={tripsForMainList}
          onEdit={modals.openEditCategory}
          onView={() => {}}
          onMoreAction={() => {}}
        />
      )}

      <AddNewListing
        selectedType={filters.selectedType}
        setSelectedType={filters.setSelectedType}
        isOpen={modals.isAddNewListingModalOpen}
        onClose={modals.closeAddNewListing}
        onContinue={modals.continueAddNewListing}
      />
      <EditCategoryModal
        visible={modals.editCategoryModal.visible}
        onClose={modals.closeEditCategory}
        onSave={modals.saveCategory}
        currentCategory={modals.editCategoryModal.trip?.categories?.[0]?.id}
        loading={modals.isSavingCategory}
      />
      {modal}
    </div>
  )
}

ExperienceListings.propTypes = {
  onSearch: PropTypes.func
}

ExperienceListings.defaultProps = {
  onSearch: () => {}
}
